/* globals requestAnimationFrame */

function normalizeCropperLayout(cropperCanvas) {
  if (cropperCanvas) {
    cropperCanvas.style.width = '100%'
    cropperCanvas.style.height = '100%'
  }
}

function centerCropperImage(cropperImage) {
  if (!cropperImage) return

  cropperImage.initialCenterSize = 'contain'

  if (typeof cropperImage.$center === 'function') {
    cropperImage.$center('contain')
  }
}

// The image can render smaller than the canvas (letterboxed/pillarboxed), so its
// bounds have to be measured, not assumed to match the canvas. cropper.js's own
// $center('contain') positions <cropper-image> with a CSS transform on the element
// itself (see its $move/$scale), so getBoundingClientRect() already reports the true,
// post-transform picture box - reading it directly can't drift from whatever cropper.js
// actually rendered, including cases like a canvas resize that doesn't itself trigger
// cropper.js to recenter (it has no ResizeObserver for that). Selection x/y/width/height
// are canvas-relative, so this subtracts the canvas's own box to match.
export function imageBoundsInCanvas(cropperImage, cropperCanvas) {
  const canvasRect = cropperCanvas.getBoundingClientRect()
  const imageRect = cropperImage.getBoundingClientRect()

  return {
    x: imageRect.left - canvasRect.left,
    y: imageRect.top - canvasRect.top,
    width: imageRect.width,
    height: imageRect.height
  }
}

function largestCenteredRect (aspectRatio, bounds) {
  let { width, height } = bounds
  if (width / height > aspectRatio) {
    width = height * aspectRatio
  } else {
    height = width / aspectRatio
  }

  return { x: bounds.x + (bounds.width - width) / 2, y: bounds.y + (bounds.height - height) / 2, width, height }
}

// Cropper.js sizes/centers the default selection against the canvas, not the image,
// so it can start (and, without this, stay draggable/resizable) partly over the
// transparent canvas area outside the image. This re-fits the initial selection to
// the image's own bounds, then rejects any later change that would move it back out.
//
// Setting `aspectRatio` schedules the library's own default sizing via its internal
// $nextTick (a single microtask, see cropperjs's `nextTick`), which would otherwise
// run *after* this function and silently overwrite an immediate correction here. So
// the correction is deferred the same way - through the selection's own $nextTick -
// to run right after it, and the bounds-rejecting listener is only attached once
// that correction has already applied, so it can't reject the library's own initial
// (out-of-bounds) placement and leave no selection at all.
async function constrainSelectionToImage (cropperSelection, cropperImage, cropperCanvas, aspectRatio) {
  // The selection rounds x/y/width/height to whole pixels (it isn't `precise`), while
  // bounds are computed here in sub-pixel floats - so a genuinely in-bounds change can
  // land up to ~1px outside the float bounds purely from that rounding. A few hundredths
  // of a pixel isn't enough slack for that and rejects legitimate moves/resizes outright.
  const EPSILON = 1
  const isWithin = (rect, bounds) => (
    rect.x >= bounds.x - EPSILON &&
    rect.y >= bounds.y - EPSILON &&
    rect.x + rect.width <= bounds.x + bounds.width + EPSILON &&
    rect.y + rect.height <= bounds.y + bounds.height + EPSILON
  )

  await cropperImage.$ready()
  await cropperSelection.$nextTick(() => {
    const bounds = imageBoundsInCanvas(cropperImage, cropperCanvas)
    const initialRect = largestCenteredRect(aspectRatio, bounds)
    cropperSelection.$change(initialRect.x, initialRect.y, initialRect.width, initialRect.height, aspectRatio)

    cropperSelection.addEventListener('change', (event) => {
      if (!isWithin(event.detail, imageBoundsInCanvas(cropperImage, cropperCanvas))) event.preventDefault()
    })
  })
}

export async function initCropper({
  imgRef,
  aspectRatio = 1.91 / 1,
  // Opt-in: constrainSelectionToImage clamps the live selection to the image's
  // true (letterbox/pillarbox-aware) bounds instead of the full canvas. The job
  // ads uploader's own crop-geometry math (computeCropData, ImageUploadWithPreview)
  // already accounts for that offset via imageBoundsInCanvas - but ImageWithCropper
  // (unit/static-page images)'s computeCropGeometry doesn't: it multiplies the
  // selection's canvas-relative x/y directly by cropperImage's own rect, assuming
  // no offset. For a letterboxed image, the constrained selection can no longer
  // reach x/y 0, and that margin would be read as a genuine crop-position shift,
  // cropping the wrong region server-side. Defaulting this off keeps that caller's
  // existing (letterboxing-unaware but internally consistent) behavior unchanged.
  constrainToImageBounds = false,
  ready = () => { },
  onCropEnd = () => { }
}) {
  imgRef.crossOrigin = 'anonymous'

  const { default: Cropper } = await import('cropperjs')
  const cropper = new Cropper(imgRef, {
    aspectRatio,
    zoomable: false,
    zoomOnWheel: false,
    zoomOnTouch: false
  })
  const { container, cropperCanvas, cropperImage, cropperSelection } =
    await waitForCropperComponents(cropper)

  normalizeCropperLayout(cropperCanvas)
  centerCropperImage(cropperImage)
  cropperSelection.aspectRatio = aspectRatio
  cropperCanvas.scaleStep = 0
  if (constrainToImageBounds) {
    await constrainSelectionToImage(cropperSelection, cropperImage, cropperCanvas, aspectRatio)
  }

  ready({ container, cropperCanvas, cropperImage, cropperSelection })

  // Fires once when user finishes cropping
  cropperCanvas.addEventListener('actionend', async (event) => {
    onCropEnd({ event, container, cropperCanvas, cropperImage, cropperSelection })
  })

  return cropper
}

function waitForCropperComponents(cropper) {
  return new Promise(resolve => {
    const wait = () => {
      const container = cropper.container
      const cropperCanvas = container.querySelector('cropper-canvas')
      const cropperImage = container.querySelector('cropper-image')
      const cropperSelection = container.querySelector('cropper-selection')

      if (!cropperCanvas || !cropperImage || !cropperSelection) {
        requestAnimationFrame(wait)
        return
      }

      resolve({ container, cropperCanvas, cropperImage, cropperSelection })
    }

    wait()
  })
}

export async function exportCroppedImage({
  type = 'image/png',
  quality = 1,
  cropperSelection,
  width = null,
  height = null
}) {
  const options = {}
  if (width) options.width = width
  if (height) options.height = height

  const canvas = await cropperSelection.$toCanvas(options)

  const result = {
    canvas,
    dataUrl: canvas.toDataURL(type, quality),
    width: canvas.width,
    height: canvas.height
  }

  return result
}
